/**
 * 路由工具函数
 * 提供路由跳转、参数解析、URL构建等工具函数
 */

import { NavigateFunction } from 'react-router-dom';
import { FRONTEND_ROUTES, ADMIN_ROUTES } from '../config/routes';

/**
 * 路由跳转工具类
 */
export class RouterUtils {
  /**
   * 跳转到前台页面
   */
  static navigateToFrontend(navigate: NavigateFunction, path: string, params?: Record<string, string | number>) {
    let url = path;
    
    // 替换路径参数
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, String(value));
      });
    }
    
    navigate(url);
  }

  /**
   * 跳转到管理后台页面
   */
  static navigateToAdmin(navigate: NavigateFunction, path: string, params?: Record<string, string | number>) {
    let url = path;
    
    // 替换路径参数
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, String(value));
      });
    }
    
    navigate(url);
  }

  /**
   * 构建带查询参数的URL
   */
  static buildUrlWithQuery(path: string, queryParams?: Record<string, string | number | boolean>) {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return path;
    }

    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  }

  /**
   * 解析URL查询参数
   */
  static parseQueryParams(search: string): Record<string, string> {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(search);
    
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    
    return params;
  }

  /**
   * 获取当前页面的基础路径（去除查询参数）
   */
  static getBasePath(pathname: string): string {
    return pathname.split('?')[0];
  }

  /**
   * 检查路径是否匹配
   */
  static isPathMatch(pathname: string, pattern: string): boolean {
    // 简单的路径匹配，支持动态参数
    const pathSegments = pathname.split('/').filter(Boolean);
    const patternSegments = pattern.split('/').filter(Boolean);
    
    if (pathSegments.length !== patternSegments.length) {
      return false;
    }
    
    return patternSegments.every((segment, index) => {
      return segment.startsWith(':') || segment === pathSegments[index];
    });
  }

  /**
   * 提取路径参数
   */
  static extractPathParams(pathname: string, pattern: string): Record<string, string> {
    const params: Record<string, string> = {};
    const pathSegments = pathname.split('/').filter(Boolean);
    const patternSegments = pattern.split('/').filter(Boolean);
    
    patternSegments.forEach((segment, index) => {
      if (segment.startsWith(':')) {
        const paramName = segment.substring(1);
        params[paramName] = pathSegments[index] || '';
      }
    });
    
    return params;
  }
}

/**
 * 前台路由跳转函数
 */
export const navigateTo = {
  home: (navigate: NavigateFunction) => RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.HOME),
  
  platformIntro: (navigate: NavigateFunction) => 
    RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.PLATFORM_INTRO),
  
  institutions: (navigate: NavigateFunction, params?: { category?: string; search?: string }) => {
    const queryParams = params ? { ...params } : {};
    const url = RouterUtils.buildUrlWithQuery(FRONTEND_ROUTES.INSTITUTIONS, queryParams);
    navigate(url);
  },
  
  institutionDetail: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.INSTITUTION_DETAIL, { id }),
  
  showcase: (navigate: NavigateFunction, params?: { category?: string; search?: string }) => {
    const queryParams = params ? { ...params } : {};
    const url = RouterUtils.buildUrlWithQuery(FRONTEND_ROUTES.SHOWCASE, queryParams);
    navigate(url);
  },
  
  showcaseDetail: (navigate: NavigateFunction, id: string | number, queryParams?: Record<string, string>) => {
    let url = FRONTEND_ROUTES.SHOWCASE_DETAIL.replace(':id', String(id));
    if (queryParams && Object.keys(queryParams).length > 0) {
      url = RouterUtils.buildUrlWithQuery(url, queryParams);
    }
    navigate(url);
  },
  
  business: (navigate: NavigateFunction) => 
    RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.BUSINESS),
  
  policy: (navigate: NavigateFunction, params?: { category?: string; search?: string }) => {
    const queryParams = params ? { ...params } : {};
    const url = RouterUtils.buildUrlWithQuery(FRONTEND_ROUTES.POLICY, queryParams);
    navigate(url);
  },
  
  policyDetail: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.POLICY_DETAIL, { id }),
  
  consultation: (navigate: NavigateFunction) => 
    RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.CONSULTATION),
  
  news: (navigate: NavigateFunction, params?: { category?: string; search?: string }) => {
    const queryParams = params ? { ...params } : {};
    const url = RouterUtils.buildUrlWithQuery('/news', queryParams);
    navigate(url);
  },
  
  newsDetail: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToFrontend(navigate, FRONTEND_ROUTES.NEWS_DETAIL, { id }),
};

/**
 * 管理后台路由跳转函数
 */
export const navigateToAdmin = {
  login: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.LOGIN),
  
  dashboard: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.DATA_MANAGEMENT),
  
  newsManagement: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.NEWS_MANAGEMENT),
  
  newsEdit: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.NEWS_EDIT, { id }),
  
  newsCreate: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.NEWS_CREATE),
  
  institutionsManagement: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.INSTITUTION_MANAGEMENT),
  
  institutionEdit: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.INSTITUTION_EDIT, { id }),
  
  institutionCreate: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.INSTITUTION_CREATE),
  
  showcaseManagement: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.SHOWCASE_MANAGEMENT),
  
  showcaseEdit: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.SHOWCASE_EDIT, { id }),
  
  showcaseCreate: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.SHOWCASE_CREATE),
  
  policyManagement: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.POLICY_MANAGEMENT),
  
  policyEdit: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.POLICY_EDIT, { id }),
  
  policyCreate: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.POLICY_CREATE),
  
  businessManagement: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.BUSINESS_MANAGEMENT),
  
  businessEdit: (navigate: NavigateFunction, id: string | number) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.BUSINESS_EDIT, { id }),
  
  businessCreate: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.BUSINESS_CREATE),
  
  userManagement: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.USER_MANAGEMENT),
  
  systemSettings: (navigate: NavigateFunction) => 
    RouterUtils.navigateToAdmin(navigate, ADMIN_ROUTES.SYSTEM_SETTINGS),
};

/**
 * 路由守卫工具
 */
export class RouteGuard {
  /**
   * 检查用户是否已登录
   */
  static isAuthenticated(): boolean {
    // 这里应该检查实际的认证状态
    const token = localStorage.getItem('admin_token');
    return !!token;
  }

  /**
   * 检查用户权限
   */
  static hasPermission(permission: string): boolean {
    // 这里应该检查用户权限
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    return userPermissions.includes(permission);
  }

  /**
   * 管理后台路由守卫
   */
  static adminGuard(pathname: string): string | null {
    // 如果访问登录页面，直接允许
    if (pathname === ADMIN_ROUTES.LOGIN) {
      return null;
    }

    // 检查是否已登录
    if (!this.isAuthenticated()) {
      return ADMIN_ROUTES.LOGIN;
    }

    // 检查权限
    if (pathname.includes('/admin/users') && !this.hasPermission('user_management')) {
      return ADMIN_ROUTES.DATA_MANAGEMENT;
    }

    return null;
  }
}
